import { useTranslation } from 'react-i18next';
import { HpButton, Kbd } from '@hp/ui';
import type { ClusterResolution, ClusterStatus, ReviewDecision } from '@hp/domain';
import { StatusBadge } from '../../components/badges';

/** Decision buttons + kbd hints + prev/next pending navigation. */
export function DecisionBar({
  canDecide,
  clusterStatus,
  clusterResolution,
  isPending,
  busy,
  overridesCount,
  deleteAll,
  onApprove,
  onNotDuplicate,
  onDeleteAll,
  onDefer,
  onReopen,
  onPrev,
  onNext,
}: {
  /** Holder of crm.review; others see the bar read-only. */
  canDecide: boolean;
  clusterStatus: ClusterStatus;
  clusterResolution: ClusterResolution;
  isPending: boolean;
  /** The decision whose request is in flight (its button pulses, the others are disabled). */
  busy: ReviewDecision | null;
  overridesCount: number;
  /** Whether "delete all" may be chosen, and the members that refuse it (server-computed). */
  deleteAll: { allowed: boolean; blockedBy: number[] };
  onApprove: () => void;
  onNotDuplicate: () => void;
  onDeleteAll: () => void;
  onDefer: () => void;
  onReopen: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const { t } = useTranslation(['review', 'common']);
  return (
    <div className="hp-card bo-decision-bar">
      <HpButton
        variant="primary"
        icon="check"
        busy={busy === 'approve_merge'}
        disabled={!isPending || !canDecide || busy !== null}
        onClick={onApprove}
      >
        {t('decision.approveMerge')} <Kbd>A</Kbd>
      </HpButton>
      <HpButton
        className="bo-danger"
        icon="close"
        busy={busy === 'not_duplicate'}
        disabled={!isPending || !canDecide || busy !== null}
        onClick={onNotDuplicate}
      >
        {t('decision.notDuplicate')} <Kbd>N</Kbd>
      </HpButton>
      {/* the tooltip lives on a wrapper: a disabled button swallows hover in most browsers */}
      <span
        title={
          deleteAll.allowed
            ? t('decision.deleteAllHint')
            : t('decision.deleteAllBlocked', { ids: deleteAll.blockedBy.map((id) => `#${id}`).join(', ') })
        }
      >
        <HpButton
          className="bo-danger"
          icon="trash"
          busy={busy === 'approve_delete'}
          disabled={!isPending || !canDecide || busy !== null || !deleteAll.allowed}
          onClick={onDeleteAll}
        >
          {t('decision.deleteAll')}
        </HpButton>
      </span>
      <HpButton
        icon="clock"
        busy={busy === 'defer'}
        disabled={!isPending || !canDecide || busy !== null}
        onClick={onDefer}
      >
        {t('decision.defer')} <Kbd>D</Kbd>
      </HpButton>
      {clusterStatus === 'approved' && (
        <HpButton icon="rotateCcw" busy={busy === 'reopen'} disabled={busy !== null || !canDecide} onClick={onReopen}>
          {t('decision.reopen')}
        </HpButton>
      )}
      {!canDecide && <span className="bo-muted bo-decision-bar__note">{t('decision.needsPermission')}</span>}
      {!isPending && (
        <span className="bo-muted bo-decision-bar__note">
          <StatusBadge status={clusterStatus} />{' '}
          {clusterStatus === 'approved'
            ? clusterResolution === 'delete'
              ? t('decision.closedApprovedDelete')
              : t('decision.closedApproved')
            : t('decision.closed')}
        </span>
      )}
      {overridesCount > 0 && <span className="bo-hint">{t('decision.overrides', { count: overridesCount })}</span>}
      <span className="bo-grow-end bo-row">
        <HpButton className="bo-button--sm" icon="chevronLeft" onClick={onPrev}>
          {t('decision.prevPending')} <Kbd>←</Kbd>
        </HpButton>
        <HpButton className="bo-button--sm" icon="chevronRight" onClick={onNext}>
          {t('decision.nextPending')} <Kbd>→</Kbd>
        </HpButton>
      </span>
    </div>
  );
}
